<template>
  <div class="paycools-kyc-page">
    <section class="kyc-content">
      <!-- 标题区 -->
      <header class="kyc-header">
        <el-button type="text" style="padding:0;" @click="$emit('requestBack')">
          <svg-icon icon-class="back" class-name="kyc-back-icon" />
        </el-button>
        <h2 class="kyc-title">{{ $t('kyc.title') }}</h2>
      </header>

      <!-- 表单区 -->
      <el-form
        ref="accountForm"
        :model="form"
        :rules="accountRules"
        label-position="left"
        label-width="260px"
        class="kyc-form"
      >
        <div v-if="rejectionItems && rejectionItems.length" class="kyc-rejection-box" style="margin-bottom:16px;">
          <el-alert
            type="error"
            :title="$t('kyc.rejection_title')"
            :closable="false"
            show-icon
          >
            <div v-for="(item, index) in rejectionItems" :key="index" class="rejection-item">
              {{ index + 1 }}. {{ item }}
            </div>
          </el-alert>
        </div>
        <!-- Bank Name -->
        <el-form-item prop="bank_name" :label="$t('kyc.account_info_bank_name')">
          <el-select
            v-model="form.bank_name"
            :placeholder="$t('kyc.account_info_placeholder_bank_name')"
            filterable
            clearable
          >
            <el-option
              v-for="item in bankOptions"
              :key="item.bank_code"
              :label="item.bank_code+ '-' + item.full_name"
              :value="item.bank_code"
            />
          </el-select>
        </el-form-item>

        <!-- Account Name -->
        <el-form-item prop="bank_account_name" :label="$t('kyc.account_info_account_name')">
          <el-input
            v-model="form.bank_account_name"
            :placeholder="$t('kyc.account_info_placeholder_account_name')"
            maxlength="100"
          />
        </el-form-item>

        <!-- Account Number -->
        <el-form-item prop="bank_account_no" :label="$t('kyc.account_info_account_number')">
          <el-input
            v-model="form.bank_account_no"
            :placeholder="$t('kyc.account_info_placeholder_account_number')"
            maxlength="100"
          />
        </el-form-item>

        <!-- Account Proof -->
        <el-form-item prop="bank_card_files" :label="$t('kyc.account_info_account_proof')" class="form-item-no-style">
          <UpdateFile
            ref="bank_card_files"
            v-model="form.bank_card_files"
            type="bank_card_files"
            :file-list="fileList"
            :uploading="uploading"
            :remove-files="removeFiles"
            :uploading-progress="uploadingProgress"
            @changeFileList="changeFileList"
            @changeUploading="changeUploading"
            @changeUploadingProgress="changeUploadingProgress"
            @changeRemoveFiles="changeRemoveFiles"
          />
        </el-form-item>
      </el-form>

      <!-- 底部按钮 -->
      <div class="kyc-footer">
        <el-button class="kyc-draft-btn" :disabled="isSubmitting" @click="handleSave">{{ $t('kyc.save_as_draft') }}</el-button>
        <el-button type="primary" class="kyc-submit-btn" :disabled="isSubmitting" @click="handleSubmit">{{ $t('kyc.submit_application') }}</el-button>
      </div>
    </section>
  </div>
</template>

<script>
import { getAccountCurrencyOptions } from '@/maps/kyc'
import { updateAccountInfo } from '@/api/kyc/kyc'
import { fetchDisbursementAllCodeList } from '@/api/components/components'

export default {
  name: 'PaycoolsKycAccountInfo',
  components: {
    UpdateFile: () => import('@/views/paycools/kyc/UpdateFile'),
    SvgIcon: () => import('@/components/SvgIcon')
  },
  props: {
    kycData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        bank_name: '',
        bank_account_name: '',
        bank_account_no: '',
        bank_card_files: null
      },
      bankOptions: [],
      accountRules: {
        bank_name: [{ required: true, message: this.$t('kyc.validation_bank_name_required'), trigger: ['blur', 'change'] }],
        bank_account_name: [
          { required: true, message: this.$t('kyc.validation_account_name_required'), trigger: ['blur', 'change'] },
          { max: 100, message: this.$t('kyc.validation_length_100'), trigger: ['blur', 'change'] }
        ],
        bank_account_no: [
          { required: true, message: this.$t('kyc.validation_account_number_required'), trigger: ['blur', 'change'] },
          { max: 100, message: this.$t('kyc.validation_length_100'), trigger: ['blur', 'change'] },
          { pattern: /^[0-9]*$/, message: this.$t('tips.validation_number_only'), trigger: ['blur', 'change'] }
        ],
        bank_card_files: [{ required: true, message: this.$t('kyc.validation_account_proof_required'), trigger: ['change'] }]
      },
      fileList: {},
      uploading: {},
      removeFiles: [],
      uploadingProgress: {},
      localSubmitting: false
    }
  },
  computed: {
    isSubmitting() {
      return this.submitting || this.localSubmitting
    },
    currencyOptions() {
      return getAccountCurrencyOptions()
    },
    rejectionItems() {
      const audit = this.kycData && this.kycData.audit_info
      if (!audit || audit.kyc_status !== 4) return []
      return (audit.audit_back_list || []).map(item => item.back_content).filter(Boolean).slice(0, 3)
    }
  },
  watch: {
    kycData: {
      handler(newData) {
        if (newData && newData.bank_card_info) {
          this.populateFormData(newData.bank_card_info)
        }
      },
      immediate: true
    },
    form: {
      deep: true,
      handler() {
        this.$emit('markChanged')
      }
    }
  },
  created() {
    this.fetchBankList()
  },
  methods: {
    changeFileList(config) {
      // handle simple upload types similar to CompanyDocument
      if (config.push) {
        const { name } = config.file
        if (!this.fileList[config.type]) this.$set(this.fileList, config.type, [])
        this.fileList[config.type].push({
          name: name || (config.file && config.file.name),
          url: config.content_oss_url,
          content: config.resData,
          content_oss_url: config.content_oss_url
        })
        this.$set(this.form, config.type, config.resData)
        this.$refs['accountForm'] && this.$refs['accountForm'].clearValidate(config.type)
        return
      } else if (config.delete && config.typeFileList.length === 0) {
        this.$set(this.form, config.type, null)
        this.$refs['accountForm'] && this.$refs['accountForm'].validateField(config.type)
      }
      const normalized = (config.typeFileList || []).map(item => ({
        name: item.name,
        url: item.url,
        content: item.content,
        content_oss_url: item.content_oss_url
      }))
      this.$set(this.fileList, config.type, normalized)
    },
    changeUploading(config) {
      this.$set(this.uploading, config.type, config.value)
    },
    changeUploadingProgress(config) {
      this.$set(this.uploadingProgress, config.type, config.value)
    },
    changeRemoveFiles(config) {
      if (config.push) {
        const { uid } = config.file
        this.removeFiles.push(uid)
        return
      }
    },
    async fetchBankList() {
      try {
        const response = await fetchDisbursementAllCodeList()
        if (response.data && Array.isArray(response.data)) {
          this.bankOptions = response.data
        }
      } catch (error) {
        console.error('Failed to fetch bank list:', error)
        this.$message.error('获取银行列表失败')
      }
    },
    async saveDraft() {
      return new Promise((resolve, reject) => {
        this.$refs['accountForm'].validate((valid, invalidateData) => {
          if (!valid) {
            // 表单验证失败，不在此处弹提示，由父组件统一处理
            reject(new Error('表单验证失败'))
            return
          }
          this.$emit('resetChanged')
          // 验证通过后执行异步保存逻辑
          this.performSave().then(resolve).catch(reject)
        })
      })
    },
    async performSave() {
      const formData = this.form || {}

      // separate fields for different processing
      const {
        ...basicFormData
      } = formData
      const bank_card_files = []
      this.fileList['bank_card_files'].forEach(item => {
        bank_card_files.push({
          type: 12,
          content: item.content,
          name: item.name
        })
      })
      // build account info payload using spread syntax (flat structure as per API)
      const payload = {
        ...basicFormData,
        // handle account proof files as bank_card_files array with type=12
        bank_card_files: bank_card_files
      }

      // prune empty values from payload
      Object.keys(payload).forEach(key => {
        const val = payload[key]
        if (val === undefined || val === null || val === '') delete payload[key]
        // also prune empty arrays
        if (Array.isArray(val) && val.length === 0) delete payload[key]
      })

      const result = await updateAccountInfo(payload)
      this.$emit('saved')
      return result
    },
    async handleSave() {
      if (this.localSubmitting) return
      this.localSubmitting = true
      try {
        await this.saveDraft()
        this.$message.success(this.$t('kyc.save_draft_success'))
      } catch (error) {
        // 错误已在 saveDraft 中处理，这里不需要额外处理
      } finally {
        this.localSubmitting = false
      }
    },
    async handleSubmit() {
      if (this.localSubmitting) return
      this.localSubmitting = true
      try {
        await this.saveDraft()
        this.$emit('submit')
      } finally {
        this.localSubmitting = false
      }
    },
    populateFormData(data) {
      this.fileList = {
        bank_card_files: [] // 授权书
      }
      // 将API数据填充到表单中
      if (data.bank_name) this.form.bank_name = data.bank_name
      if (data.bank_account_name) this.form.bank_account_name = data.bank_account_name
      if (data.bank_account_no) this.form.bank_account_no = data.bank_account_no

      // 处理 bank_card_files 数组，找到 type=12 的账户证明文件
      const companyFiles = data.bank_card_files || []

      const typeMapping = {
        12: 'bank_card_files' // 授权书
      }

      // Process each company file
      companyFiles.forEach(file => {
        const formKey = typeMapping[file.type]
        if (formKey) {
          this.fileList[formKey].push({
            name: file.name || `${formKey}.pdf`,
            url: file.content_oss_url || file.content,
            content: file.content,
            content_oss_url: file.content_oss_url || file.content
          })
          this.$set(this.form, formKey, file.content)
          this.$refs['form'] && this.$refs['form'].clearValidate(formKey)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.paycools-kyc-page {
  display: flex;
  align-items: stretch;
  background-color: #f5f7fb;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
}

.kyc-sidebar {
  width: 240px;
  background-color: #f7f8fc;
  border-radius: 8px;
  padding: 24px 0;
  box-shadow: 0 0 0 1px #edf0f7;
}

.kyc-step-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.kyc-step-item {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  font-size: 14px;
  color: #7b8192;
  cursor: default;
}

.kyc-step-item.is-active {
  background-color: #ffffff;
  color: #2f3a60;
  font-weight: 600;
  border-left: 3px solid #3b82f6;
}

.kyc-step-index {
  margin-right: 8px;
}

.kyc-step-label {
  flex: 1;
}

.kyc-content {
  flex: 1;
  margin-left: 32px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 32px 40px 40px;
  box-shadow: 0 8px 20px rgba(15, 35, 95, 0.04);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.kyc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.kyc-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.kyc-subtitle {
  margin: 8px 0 20px;
  font-size: 14px;
  color: #6b7280;
}

.kyc-id-type {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #c7d2fe;
  max-width: 480px;
}

.kyc-id-type-btn {
  flex: 1;
  border: none;
  border-radius: 0;
  font-weight: 500;
}

.kyc-id-type-btn.is-active {
  background-color: #3b82f6;
  color: #ffffff;
}

.kyc-id-type-btn.is-ghost {
  background-color: #ffffff;
  color: #374151;
}

.kyc-form {
  max-width: 920px;
  flex: 1;
}

.kyc-upload-single {
  width: 100%;
  max-width: 600px;
}

.kyc-upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background-color: #fafafa;
}

.kyc-upload-icon {
  font-size: 32px;
  color: #9ca3af;
  margin-bottom: 12px;
  font-weight: 300;
}

.kyc-upload-main-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.kyc-upload-sub-text {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  line-height: 1.5;
}

.kyc-upload-tip {
  font-size: 12px;
  color: #9ca3af;
}

.kyc-footer {
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
  align-items: center;
}

.kyc-draft-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background-color: #ffffff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  border-radius: 6px;
}

.kyc-submit-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
}

.rejection-item {
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}
</style>
